import { ReactNode } from 'react';
import PercentageGraphIcon from '../../../assets/icons/percentage-graph.svg?react';
import PeriodGraphIcon from '../../../assets/icons/period-graph.svg?react';
import SpikesGraphIcon from '../../../assets/icons/spikes-graph.svg?react';
import { SidePanel } from '../../SidePanel';

interface TriggerLogicDocumentationPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function TriggerLogicDocumentationPanel({
  open,
  onOpenChange,
}: TriggerLogicDocumentationPanelProps) {
  return (
    <SidePanel
      open={open}
      onOpenChange={onOpenChange}
      title='Trigger types'
      subtitle='Documentation'
      width='md'
      className='bg-surface-primary px-4 py-4 md:px-6 md:py-4'
    >
      <div className='flex flex-col gap-6'>
        <TriggerLogicDocBlock
          title='Percentage: Mostly above'
          description='Triggers when the value stays above the limit for a certain percentage of the selected time window.'
          graph={<PercentageGraphIcon className='h-33 w-full text-primary' />}
          statBlocks={[
            { label: 'Percentage threshold', value: '75%' },
            { label: 'Evaluation window', value: '5 min' },
            { label: 'Cooldown period', value: '5 min' },
          ]}
          bestFor='Detecting long-lasting load, performance pressure, or sustained overuse.'
        />

        <TriggerLogicDocBlock
          title='Period: One long block'
          description='Triggers when the value stays above the limit for a continuous, uninterrupted time period.'
          graph={<PeriodGraphIcon className='h-33 w-full text-primary' />}
          statBlocks={[
            { label: 'Period duration', value: '5 min' },
            { label: 'Cooldown period', value: '5 min' },
          ]}
          bestFor='Detecting constant issues that should never happen for long periods.'
        />

        <TriggerLogicDocBlock
          title='Spikes: Multiple hits'
          description='Triggers when the value exceeds the limit a specific number of times within a time window.'
          graph={<SpikesGraphIcon className='h-33 w-full text-primary' />}
          statBlocks={[
            { label: 'Number of spikes', value: '5 spikes' },
            { label: 'Evaluation window', value: '5 min' },
            { label: 'Cooldown period', value: '5 min' },
          ]}
          bestFor='Detecting recurring bursts that indicate intermittent instability.'
          showDivider={false}
        />
      </div>
    </SidePanel>
  );
}

function TriggerLogicDocBlock({
  title,
  description,
  graph,
  statBlocks,
  bestFor,
  showDivider = true,
}: {
  title: string;
  description: string;
  graph: ReactNode;
  statBlocks: Array<{ label: string; value: string }>;
  bestFor: string;
  showDivider?: boolean;
}) {
  return (
    <section
      className={`flex flex-col gap-3 ${showDivider ? 'border-border-primary border-b pb-6' : ''}`}
    >
      <h3 className='size-p1 text-primary'>{title}</h3>
      <p className='size-p2 text-primary'>{description}</p>

      <div className='bg-surface-primary-support border-border-secondary rounded-lg border p-3'>
        {graph}
      </div>

      <div className='grid gap-2 sm:grid-cols-3'>
        {statBlocks.map((stat) => (
          <div
            key={stat.label}
            className='bg-surface-primary-support border-border-secondary flex flex-col rounded-md border p-2'
          >
            <span className='size-caption text-secondary'>{stat.label}</span>
            <span className='size-p2 text-primary font-bold'>{stat.value}</span>
          </div>
        ))}
      </div>

      <p className='size-p2 text-primary'>
        <span className='font-bold'>Best for:</span> {bestFor}
      </p>
    </section>
  );
}
